import type { FragmentType } from 'src/gql';
import { isNil } from 'es-toolkit';
import { Landmark, TrendingUp } from 'lucide-react';
import { SAVVY_WOB_URL } from 'src/constants/logo-urls/savvy-wob-logo';
import { graphql, unmaskFragment } from 'src/gql';
import { renderProviderImg } from 'src/utils/image';

import type { ProviderAccountSource } from '@savvy/constants/generated/gql-enums';
import type { Account, PortfolioItemGroupName } from '@savvy/constants/generated/gql-types';
import type { AuthContextValue } from '@savvy/deprecated-shared/contexts/jwt-context.context';
import type { ID } from '@savvy/utils/id';
import { financialAccountIconColors } from '@savvy/deprecated-shared/theme';
import { accountSubtypeDisplayName } from '@savvy/deprecated-shared/utils/portfolio';

import { formatItemValue, renderItemIcon } from './portfolio-item-display';

export const DISCONNECTED_SOURCE_STATUSES: readonly string[] = [
  'ITEM_LOGIN_REQUIRED',
  'FAILED',
  'NO_ACCOUNTS',
  'PASSWORD_RESET_REQUIRED',
];

/**
 * @deprecated Please use IsDisconnectedLinkedAccount instead
 */
export function deprecated_isDisconnectedLinkedAccount(account: Account) {
  return DISCONNECTED_SOURCE_STATUSES.includes(account.providerAccount?.sourceStatus || '');
}
export const IsDisconnectedLinkedAccountFragment = graphql(/* GraphQL */ `
  fragment IsDisconnectedLinkedAccount on Account {
    providerAccount {
      sourceStatus
    }
  }
`);
export function isDisconnectedLinkedAccount({
  accountFrag,
}: {
  accountFrag: FragmentType<typeof IsDisconnectedLinkedAccountFragment>;
}) {
  const account = unmaskFragment(IsDisconnectedLinkedAccountFragment, accountFrag);
  return DISCONNECTED_SOURCE_STATUSES.includes(account.providerAccount?.sourceStatus || '');
}

export function isPlaidLinkedAccount({
  providerAccountSource,
}: {
  providerAccountSource: ProviderAccountSource | null | undefined;
}) {
  return providerAccountSource === 'PLAID';
}

const PROVIDER_SOURCE_DISPLAY_NAMES: Record<ProviderAccountSource, string> = {
  CHIMNEY: 'Chimney',
  MONEYKIT: 'MoneyKit',
  ORION: 'Orion',
  PLAID: 'Plaid',
};

export function providerSourceDisplayName(source: ProviderAccountSource | null | undefined): string {
  return (source && PROVIDER_SOURCE_DISPLAY_NAMES[source]) || 'Plaid';
}

export const getManagedAccountURI = (accountId: ID, advisorView?: boolean, salesforceAccountId?: null | ID) => {
  if (advisorView && salesforceAccountId) {
    // New Advisor Dashboard

    return `/advisor/dashboard/client/${salesforceAccountId}/portfolio/account/${accountId}`;
  }
  // Client portfolio view

  return `/portfolio/accounts/${accountId}`;
};

export const getHeldawayAccountURI = (
  account: {
    id: ID;
  },
  advisorView: boolean | null | undefined,
  salesforceAccountId: ID | null | undefined,
) => {
  if (advisorView && salesforceAccountId) {
    return getHeldawayAccountURIForAdvisors(account, salesforceAccountId);
  }
  return getHeldawayAccountURIForClients(account);
};

const getHeldawayAccountURIForAdvisors = (account: { id: ID }, salesforceAccountId?: null | ID) => {
  return `/advisor/dashboard/client/${salesforceAccountId}/portfolio/external-account/${account.id}`;
};
const getHeldawayAccountURIForClients = (account: { id: ID }) => {
  return `/portfolio/external-accounts/${account.id}`;
};

export const IsLinkedAccountFragment = graphql(/* GraphQL */ `
  fragment IsLinkedAccount on Account {
    providerAccount {
      __typename
    }
    providerAccountId
  }
`);
export const isLinkedAccount = (accountFrag: FragmentType<typeof IsLinkedAccountFragment>) => {
  const account = unmaskFragment(IsLinkedAccountFragment, accountFrag);
  return account.providerAccount || account.providerAccountId;
};

export const isAdvisor = (auth: AuthContextValue) => {
  return auth.user?.profileType === 'ADVISOR';
};

export function padBulletsToMaskedAccountNumber(mask: string) {
  return mask.padStart(6, '•');
}

export function maskedAccountNumber(accountNumber: string | null | undefined): string | null {
  if (!accountNumber) return null;
  const last4 = accountNumber.slice(-4);
  return `••••${last4}`;
}

export function maskAccountMask(mask: string | null | undefined): string | null {
  if (!mask) return null;
  return `***${mask}`;
}

export const RenderIconForAccountFragment = graphql(/* GraphQL */ `
  fragment RenderIconForAccount on Account {
    custody
    offeredBySavvy
    providerLogoUrl
    type
  }
`);

export function renderIconForAccount({
  accountFrag,
  size = 40,
}: {
  accountFrag: FragmentType<typeof RenderIconForAccountFragment>;
  size?: number;
}) {
  const account = unmaskFragment(RenderIconForAccountFragment, accountFrag);
  if (account.custody === 'managed' || account.offeredBySavvy) {
    return renderProviderImg(SAVVY_WOB_URL, size);
  }
  if (account.providerLogoUrl) {
    return renderProviderImg(account.providerLogoUrl, size);
  }
  if (account.type === 'investment') {
    return renderItemIcon(financialAccountIconColors.investmentAccount, <TrendingUp color="#fff" size={20} />);
  }

  return renderItemIcon(financialAccountIconColors.bankAccount, <Landmark color="#fff" size={20} />);
}

export const AccountDeleteButtonTextFragment = graphql(/* GraphQL */ `
  fragment AccountDeleteButtonText on Account {
    ...IsLinkedAccount
    providerAccount {
      source
    }
    providerAccountId
    subtype
    type
  }
`);
export function accountDeleteButtonText(accountFrag: FragmentType<typeof AccountDeleteButtonTextFragment>) {
  const account = unmaskFragment(AccountDeleteButtonTextFragment, accountFrag);
  if (isLinkedAccount(account)) {
    return 'Remove account';
  }

  if (account.type === 'investment') {
    return 'Delete investment account';
  } else if (account.type === 'depository') {
    return 'Delete bank account';
  }
  return 'Delete account';
}

export const AccountTitleFragment = graphql(/* GraphQL */ `
  fragment AccountTitle on Account {
    accountName
    custody
    officialName
    providerAccount {
      providerName
    }
    subtype
    type
  }
`);

export function accountTitle({ accountFrag }: { accountFrag: FragmentType<typeof AccountTitleFragment> }) {
  const account = unmaskFragment(AccountTitleFragment, accountFrag);
  if (account.providerAccount && account.custody !== 'managed' && account.type === 'investment') {
    if (account.subtype && account.providerAccount.providerName) {
      return account.officialName || account.accountName;
    }
    return account.providerAccount.providerName;
  }
  return account.officialName || account.accountName;
}

export const AccountSubtitleFragment = graphql(/* GraphQL */ `
  fragment AccountSubtitle on Account {
    accountName
    custody
    loanProvider
    offeredBySavvy
    officialName
    providerAccount {
      providerName
    }
    subtype
    type
  }
`);
export function accountSubtitle({ accountFrag }: { accountFrag: FragmentType<typeof AccountSubtitleFragment> }) {
  const account = unmaskFragment(AccountSubtitleFragment, accountFrag);
  if (account.offeredBySavvy) {
    const provider =
      account.type === 'loan' && account.loanProvider ? account.loanProvider : account.providerAccount?.providerName;
    return !isNil(provider) ? `${provider} • Offered through Savvy` : 'Offered through Savvy';
  }
  if (account.custody === 'managed' || account.custody === 'unmanaged') {
    const providerName = account.providerAccount?.providerName;
    return !isNil(providerName)
      ? `${accountSubtypeDisplayName(account.subtype)} • ${providerName}`
      : accountSubtypeDisplayName(account.subtype);
  }

  if (account.providerAccount?.providerName) {
    return account.providerAccount.providerName;
  }

  if (account.subtype && account.subtype !== 'other') {
    if (account.providerAccount?.providerName) {
      return account.officialName || account.accountName;
    }
    return accountSubtypeDisplayName(account.subtype);
  }
  if (!account.providerAccount?.providerName) {
    return null;
  }
  return `${account.providerAccount.providerName} ${accountSubtypeDisplayName(account.subtype)}`;
}

export function accountValueTitle({
  account,
  portfolioItemGroupName,
}: {
  account: {
    displayBalance: {
      amount: string;
    };
  };
  portfolioItemGroupName: PortfolioItemGroupName;
}) {
  if (portfolioItemGroupName === 'OTHER_LIABILITIES') {
    return formatItemValue(-Number(account.displayBalance.amount));
  }
  return formatItemValue(account.displayBalance.amount);
}
